import type { Tag } from '@/api/v4/organization-enrich.api';
import { styled } from 'goober';
import { Button } from '@/ui/button/button';
import { SkeletonText } from '@/ui/table/infinite-table/skeleton-row';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { PopperComponent } from '@/components/popper/popper-component';
import { Stack } from '@/ui/line/line';
import { useTranslation } from 'react-i18next';
import { fonts } from '@/theme/typography';
import { TagLink } from '@/components/tag/tag-link';

export const TagsPreview = ({
  tags,
  baseLinkUrl,
  isLoading = false,
  maxVisibleTags = 3,
}: {
  tags: Tag[];
  baseLinkUrl?: string;
  isLoading?: boolean;
  maxVisibleTags?: number;
}) => {
  const { t } = useTranslation('default', {
    keyPrefix: 'globalSearch.suggestionsTable',
  });
  const visibleTags = tags.slice(0, maxVisibleTags);
  const hiddenTags =
    tags.length > maxVisibleTags ? tags.slice(maxVisibleTags) : [];

  const showMoreText = `+${hiddenTags?.length}`;

  return (
    <TagsContainer name="tags-preview-container" alignItems="center" gap="6px">
      {isLoading ? (
        <SkeletonText width="80px" />
      ) : (
        <>
          {visibleTags.map(tag => (
            <TagLink
              key={tag.id}
              id={tag.id}
              name={tag.name}
              baseUrl={baseLinkUrl}
            />
          ))}
          {hiddenTags.length > 0 && (
            <PopperComponent
              placement="top"
              visibleElement={
                <ShowMoreButton variant="text">{showMoreText}</ShowMoreButton>
              }
              PopperContent={
                <HiddenTagsContainer gap="10px">
                  <TooltipTitle>{t`tags`}</TooltipTitle>
                  <Flexbox name="hidden-tags-popper" wrap="wrap">
                    {hiddenTags.map(tag => (
                      <TagLink
                        key={tag.id}
                        id={tag.id}
                        name={tag.name}
                        baseUrl={baseLinkUrl}
                        size="xs"
                      />
                    ))}
                  </Flexbox>
                </HiddenTagsContainer>
              }
            />
          )}
        </>
      )}
    </TagsContainer>
  );
};

const TagsContainer = styled(Flexbox)`
  flex-wrap: nowrap;
`;

const ShowMoreButton = styled(Button)`
  color: ${({ theme }) => theme.colors.gray.c13};
`;

const HiddenTagsContainer = styled(Stack)`
  max-width: 400px;
  background-color: ${({ theme }) => theme.colors.basics.white};
  padding: 15px 20px;
  border-radius: 4px;
`;

export const TooltipTitle = styled('h6')`
  font-family: ${fonts.wallop};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
`;
